import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logoWhite from "@/assets/logo-white.webp";

const navLinks = [
  { label: "Etusivu", path: "/" },
  { label: "Kuntosalit", path: "/kuntosalit" },
  { label: "Hinnasto", path: "/hinnasto" },
  { label: "Valmennus", path: "/valmennus" },
  { label: "Aukiolot", path: "/aukiolot" },
  { label: "Hieronta", path: "/hieronta" },
  { label: "Yhteystiedot", path: "/yhteystiedot" },
];

const WEBSTORE_URL = "https://avoinna24.fi/takomo/webstore";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="top-0 left-0 right-0 z-50 bg-background border-b border-border/50">
        <div className="container mx-auto flex items-center justify-between h-16 md:h-20 px-4">
          <Link to="/">
            <img src={logoWhite} alt="Takomo" width={173} height={56} className="h-10 md:h-14 w-auto object-contain" />
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                className={`font-display text-sm tracking-widest uppercase transition-colors hover:text-foreground ${
                  location.pathname === l.path ? "text-foreground" : "text-foreground/60"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <a
              href={WEBSTORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary-premium px-5 py-2 rounded text-sm"
            >
              Verkkokauppa
            </a>
          </div>

          {/* Mobile toggle (in navbar) */}
          <button className="lg:hidden text-foreground" onClick={() => setOpen(!open)}>
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-background border-b border-border overflow-hidden"
            >
              <div className="flex flex-col gap-1 p-4">
                {navLinks.map((l) => (
                  <Link
                    key={l.path}
                    to={l.path}
                    onClick={() => setOpen(false)}
                    className={`font-display text-lg tracking-wider uppercase py-3 px-4 rounded transition-colors ${
                      location.pathname === l.path
                        ? "text-foreground bg-muted"
                        : "text-foreground/60 hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {l.label}
                  </Link>
                ))}
                <a
                  href={WEBSTORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary-premium px-5 py-3 rounded text-center text-lg mt-2"
                >
                  Verkkokauppa
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Floating mobile pill - visible when scrolled past navbar */}
      <AnimatePresence>
        {scrolled && !open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -10 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(true)}
            className="lg:hidden fixed top-4 right-4 z-[60] flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border/50 shadow-lg backdrop-blur-sm"
            style={{ boxShadow: "var(--shadow-card)" }}
          >
            <Menu size={18} className="text-foreground" />
            <span className="font-display text-xs tracking-widest uppercase text-foreground">Menu</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating fullscreen mobile menu overlay */}
      <AnimatePresence>
        {scrolled && open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-[60] bg-background/95 backdrop-blur-md flex flex-col"
          >
            <div className="flex justify-end p-4">
              <button onClick={() => setOpen(false)} className="text-foreground p-2">
                <X size={28} />
              </button>
            </div>
            <div className="flex flex-col gap-1 p-6 pt-2">
              {navLinks.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  onClick={() => setOpen(false)}
                  className={`font-display text-xl tracking-wider uppercase py-3 px-4 rounded transition-colors ${
                    location.pathname === l.path
                      ? "text-foreground bg-muted"
                      : "text-foreground/60 hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={WEBSTORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary-premium px-5 py-3 rounded text-center text-lg mt-4"
              >
                Verkkokauppa
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
